import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import {
  UserPlus, CalendarCheck2, ClipboardList, HeartHandshake, TrendingUp, Users,
  GraduationCap, ShieldCheck, ArrowRight, CheckCircle2, Quote, Compass, Milestone
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F57C20]"/>
    {children}
  </span>
);

const JOURNEY = [
  {
    range: "Day 1 – 30",
    title: "Foundations",
    icon: ClipboardList,
    points: ["Company, culture & compliance orientation", "Tools, systems & access provisioning", "Role fundamentals & core skill baseline"]
  },
  {
    range: "Day 31 – 60",
    title: "Applied Practice",
    icon: Compass,
    points: ["Guided, real-world tasks with a mentor", "Team shadowing & cross-functional exposure", "First milestone deliverable signed off"]
  },
  {
    range: "Day 61 – 90",
    title: "Independent Contribution",
    icon: Milestone,
    points: ["Owns tasks with minimal supervision", "Structured feedback & performance check-in", "Fully ramped into the role"]
  }
];

const ROLE_TRACKS = [
  { title: "Engineering & Product", desc: "Codebase walkthroughs, dev-environment setup, architecture deep-dives and paired programming.", icon: Users },
  { title: "Sales & Customer Success", desc: "Product pitch mastery, CRM proficiency and shadowed client calls before first solo pitch.", icon: HeartHandshake },
  { title: "Operations & Support", desc: "Process playbooks, tooling certification and live-queue shadowing with a senior buddy.", icon: ShieldCheck },
  { title: "Leadership & Management", desc: "Stakeholder mapping, team charter workshops and a 90-day leadership action plan.", icon: GraduationCap }
];

const METRICS = [
  { value: "45%", label: "Faster Time-to-Productivity" },
  { value: "90%", label: "90-Day Retention Rate" },
  { value: "4.7/5", label: "New-Hire Experience Score" },
  { value: "60%", label: "Fewer Manager Escalations" }
];

const NewHireTrainingPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0A1F5C] via-[#15348C] to-[#1E47A8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "26px 26px" }}
        />
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-[100px]"/>
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-[100px]"/>
        <div className="relative max-w-6xl mx-auto px-4 py-24 lg:py-28 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#8BD835] bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-7">
              <UserPlus size={14}/> NEW HIRE TRAINING
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold leading-[1.1]">A structured first 90 days, every single time.</h1>
            <p className="mt-5 text-gray-300 text-base lg:text-lg leading-relaxed">
              Replace inconsistent, tribal-knowledge onboarding with a standardised, role-based ramp-up journey &mdash; so every new joiner reaches full productivity faster.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Design Our Onboarding Plan <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-4">SNAPSHOT</div>
              <div className="grid grid-cols-2 gap-5">
                {METRICS.map(m => (
                  <div key={m.label}>
                    <div className="text-2xl font-bold">{m.value}</div>
                    <div className="text-xs text-gray-300 mt-1">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 30-60-90 Journey */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>THE 30-60-90 DAY JOURNEY</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">A clear path from Day 1 to fully ramped.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6 relative">
            <div className="hidden lg:block absolute top-14 left-[16.5%] right-[16.5%] h-0.5 bg-gradient-to-r from-[#6BBE32] via-[#F57C20] to-[#15348C]"/>
            {JOURNEY.map((j, i) => (
              <Reveal key={j.title} variant="up" delay={i * 100}>
                <div className="sg-card relative bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#15348C] flex items-center justify-center mb-5 relative z-10">
                    <j.icon size={22} className="text-white"/>
                  </div>
                  <div className="text-xs font-bold tracking-widest text-[#F57C20]">{j.range}</div>
                  <h3 className="text-lg font-bold text-[#15348C] mt-1">{j.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {j.points.map(p => (
                      <li key={p} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                        <CheckCircle2 size={15} className="text-[#6BBE32] mt-0.5 shrink-0"/>{p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Role tracks */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>ROLE-BASED TRACKS</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Onboarding built for the role, not one-size-fits-all.</h2>
              <p className="mt-3 text-gray-600">Every function ramps differently &mdash; our tracks reflect the tools, stakeholders and milestones specific to each team.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROLE_TRACKS.map((r, i) => (
              <Reveal key={r.title} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <r.icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <h4 className="font-bold text-[#15348C] text-lg">{r.title}</h4>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Manager & buddy support + testimonial */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-5 gap-6">
          <Reveal variant="left" className="lg:col-span-2">
            <div className="bg-gradient-to-br from-[#0A1F5C] to-[#1E47A8] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <CalendarCheck2 size={26} className="text-[#8BD835] mb-4"/>
                <h3 className="text-xl font-bold text-white">Manager &amp; buddy support, built in.</h3>
                <p className="mt-3 text-sm text-gray-300 leading-relaxed">Every new hire is paired with a trained buddy and a structured manager check-in cadence at Day 7, 30, 60 and 90 &mdash; no ramp-up left to chance.</p>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-3">
            <div className="bg-[#F8F9FA] border border-gray-200/80 rounded-2xl p-8 lg:p-10 h-full flex flex-col shadow-sm">
              <Quote size={30} className="text-[#F57C20] mb-4"/>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Our new hires used to take five to six months to become fully productive. With SkillsGain&rsquo;s structured onboarding tracks, that&rsquo;s down to under ten weeks &mdash; and attrition in the first year has dropped sharply.
              </p>
              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="font-bold text-[#15348C]">Head of People Operations</div>
                <div className="text-sm text-gray-500">Global IT Services Enterprise</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR HR &amp; TALENT TEAMS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Give every new hire a confident start.</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Let&rsquo;s map your roles, tools and milestones into a ready-to-launch onboarding journey.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Role-based tracks", "Manager & buddy playbooks", "Ramp-up analytics"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Onboarding Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default NewHireTrainingPage;
